import i18n from "i18next";
import k from "src/i18n/keys";
import { connect, useSelector, useDispatch } from "react-redux";
import Link from "redux-first-router-link";
import React, {
  useState,
  useLayoutEffect,
  useRef,
  useMemo,
  useEffect,
  useContext,
} from "react";
import { FilterWorkerContext } from "src/contexts/FilterWorkerContext";
import * as R from "ramda";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel as getDefaultFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  type ColumnFiltersState,
  type PaginationState,
  type SortingState,
} from "@tanstack/react-table";
import { useDebouncedCallback } from "use-debounce";
import { Text, Flex, Button } from "@chakra-ui/react";

import FormName from "src/components/Form/Name";
import Row from "./Row";
import Loader from "./RowLoader";
import RenderingLoader from "./Loader";
import NoValue from "./NoValue";
import VirtualList from "src/components/VirtualList";
import Filters from "../Filters";
import MenuBar from "../MenuBar";
import HeaderItem from "./HeaderItem";
import ChecklistHeaderItem from "./ChecklistItem";
import SelectAll from "./SelectAll";
import {
  Header as StyledHeader,
  Table as StyledTable,
  TableWrapper,
  EmbeddedFieldsHeader,
  NestedTable,
  FormFieldsHeader,
  Footer,
  PageButton,
  PageIndicatorContainer,
  PageInput,
  Selector,
} from "./styles";
import {
  size,
  checklistFieldSizeMap,
  omitedFields,
  manageViewFieldWidthMap,
} from "src/constants/processInstanceColumns";
import { dataStages } from "src/constants";
import { getAllEmbeddedFields } from "src/actions/reports";
import {
  getEmbeddedFormColumns,
  getEmbeddedConversationColumns,
  getWorkflowInstances,
  getWorkflowInstanceFilter,
  getPrincipalChecklistFields,
  getProcessTableColumns,
  getReportsTableColumns,
  getExpandedFields,
  getUserNames,
  getTemplateStatus,
  getShowVersionInfo,
  getChecklistFieldsById,
  getFieldSizeMap,
  getWorkflowInstancesById,
  getAllRecords,
  getAreInstancesFormatted,
  getAdditionalFiltersChartId,
  getCurrentUser,
  getWorkflowSettings,
  getBulkUpdateStatus,
  getComponentPermission,
} from "src/reducers";
import { convertToTitleCase } from "src/utils";
import Search from "../Search";
import { getHeaderColumnSettings } from "./manageViewFilters";
import {
  setManageViewFilter,
  filterEmbeddedFields,
  clearAllRecordsFilter,
  filterReportInstances,
} from "src/actions/workflows";
import * as colors from "src/styles/constants/colors";
import { setReports } from "src/actions/reports";
import * as styles from "./styles";
import {
  setNewChartDialogAttributes,
  closeNewChartDialog,
} from "src/actions/chart";
import { componentPermissions } from "src/constants/roleManagement";

import type {
  Action,
  AllRecords,
  AppState,
  DataStage,
  FieldsById,
  ManageViewShowVersions,
  ProcessTemplateId,
  TemplateStatusById,
  UnifizeUser,
  Workflow,
  WorkflowInstances,
  WorkflowInstanceFilter,
} from "src/types";
import type { ManageViewColumn, Nullable } from "src/ts-types";
import type {
  TableRow,
  HeaderItemProps,
  ChecklistHeaderItemProps,
  SearchProps,
  MenuBarProps,
  TextProps,
  FlexProps,
  ButtonProps,
  ManageViewColumnDef,
} from "./flow-bridge-types";

interface Props {
  reportId?: Nullable<string>;
  processColumns: Array<ManageViewColumn>;
  reportsColumn: Array<ManageViewColumn>;
  fields: { [index: number]: unknown };
  expandedFields: Record<string, boolean>;
  instances: Array<WorkflowInstances>;
  userNames: Record<string, { displayName?: string } | undefined>;
  setManageViewFilter: typeof setManageViewFilter;
  clearAllRecordsFilter: typeof clearAllRecordsFilter;
  filter: WorkflowInstanceFilter;
  templateStatus: TemplateStatusById | null;
  showVersions: ManageViewShowVersions;
  embeddedFormColumns: object;
  embeddedConversationColumns: object;
  fieldsById: FieldsById;
  fieldSizeMap: Record<string, string>;
  workflowSettings: Workflow["settings"];
  _getAllEmbeddedFields: (templateId: ProcessTemplateId) => Action;
  instancesById: Record<string, WorkflowInstances>;
  _filterEmbeddedFields: ({
    instances,
  }: {
    instances: Record<string, WorkflowInstances>;
  }) => Action;
  allRecords: AllRecords;
  areInstancesFormatted?: boolean;
  bulkUpdateStatus: DataStage;
  currentUser: UnifizeUser;
}

const moveRevisionsField = (processColumns: Array<ManageViewColumn>) => {
  const revisionFieldIndex = processColumns.findIndex(
    (item) => item?.type === "revision",
  );

  if (revisionFieldIndex === -1) return processColumns;

  const titleIndex = processColumns.findIndex((item) => item?.key === "title");

  return R.move(revisionFieldIndex, titleIndex + 1, processColumns);
};

const getChecklistFieldSize = (fieldType?: string): string => {
  if (fieldType && fieldType in checklistFieldSizeMap) {
    return checklistFieldSizeMap[
      fieldType as keyof typeof checklistFieldSizeMap
    ];
  }
  return "230px";
};

const getStandardFieldSize = (key?: string): string => {
  if (key && key in size) {
    return size[key as keyof typeof size];
  }
  return "230px";
};

const getManageViewFieldSize = (key?: string): string | undefined => {
  if (key && key in manageViewFieldWidthMap) {
    return manageViewFieldWidthMap[key as keyof typeof manageViewFieldWidthMap];
  }
  return undefined;
};

const VirtualListComponent = VirtualList as unknown as React.ComponentType<{
  initialAmount: number;
  progressiveAmount: number;
  rowCount: number;
  renderItem: (index: number) => React.ReactNode;
  renderLoader?: () => React.ReactNode;
  role?: string;
}>;

const FormFieldsHeaderComponent =
  FormFieldsHeader as unknown as React.ComponentType<{
    width?: number | string;
    backgroundColor?: string;
    children?: React.ReactNode;
  }>;

/* Flow bridge-types */

// Chakra components have malformed complex type unions that produce errors
// so we're using these type shims instead
const ChakraText = Text as unknown as React.ComponentType<TextProps>;
const FlexComponent = Flex as unknown as React.ComponentType<FlexProps>;
const ChakraButton = Button as unknown as React.ComponentType<ButtonProps>;

const HeaderItemComponent =
  HeaderItem as unknown as React.ComponentType<HeaderItemProps>;
const ChecklistHeaderItemComponent =
  ChecklistHeaderItem as unknown as React.ComponentType<ChecklistHeaderItemProps>;
const SearchComponent = Search as unknown as React.ComponentType<SearchProps>;
const MenuBarComponent =
  MenuBar as unknown as React.ComponentType<MenuBarProps>;
const LinkComponent = Link as unknown as React.ComponentType<{
  to: unknown;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}>;
const StyledTableComponent = StyledTable as unknown as React.ComponentType<
  React.TableHTMLAttributes<HTMLTableElement> & {
    columnSize: string;
    ref?: React.Ref<HTMLTableElement>;
  }
>;

const Table = ({
  reportId,
  processColumns,
  reportsColumn,
  fields,
  expandedFields,
  instances,
  userNames,
  setManageViewFilter,
  clearAllRecordsFilter,
  _getAllEmbeddedFields,
  filter,
  templateStatus,
  showVersions,
  embeddedFormColumns,
  embeddedConversationColumns,
  fieldsById,
  fieldSizeMap,
  instancesById,
  workflowSettings,
  currentUser,
  _filterEmbeddedFields,
  allRecords,
  areInstancesFormatted,
  bulkUpdateStatus,
}: Props) => {
  const dispatch = useDispatch();
  const filterWorker = useContext(FilterWorkerContext);
  const chartId = useSelector(({ app }: { app: AppState }) =>
    getAdditionalFiltersChartId(app),
  );
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [width, setWidth] = useState("100%");
  const [allRevisionsShown, setRevisionsShown] = useState(false);
  const columnResizeMode = "onEnd" as const;
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 16,
  });
  const manageViewAllowed = useSelector(({ app }: { app: AppState }) =>
    getComponentPermission(app, componentPermissions.manage),
  );

  /**
   * Returns all process instances with an attribute added
   * (`ownerDisplayName`) for the owner's name and excludes cancelled and
   * archived conversations unless user is filtering by including it
   * @param {object[]} instances - all process instances.
   * @param {object} filterParams - current filter's state.
   * @param {object} userNames - UID mapped to `displayName`
   * @param {object} templateStatus - statusId mapped to status data
   */
  const getInstances = (
    instances: Array<WorkflowInstances>,
    filterParams: Record<string, unknown>,
    userNames: Record<string, { displayName?: string } | undefined>,
    templateStatus: TemplateStatusById | null,
  ): Array<TableRow> => {
    let allInstances = instances.map((instance, index) => ({
      ...instance,
      ownerDisplayName: userNames[instance.owner || ""]?.displayName || "",
      index,
    }));

    const statusFilters = (
      filterParams as unknown as { status?: Array<number | string> }
    ).status;

    if (!statusFilters?.includes(-3)) {
      allInstances = allInstances.filter((instance) => {
        if (instance.status === -3) {
          return false;
        }
        return true;
      });
    }

    // Keep legacy behavior: "includeArchived" can exist inside `status`
    if (!statusFilters?.includes("includeArchived")) {
      allInstances = allInstances.filter((instance) => {
        return (
          !instance.status ||
          (typeof instance.status === "number" &&
            templateStatus?.[`${instance.status}`]?.archiveMode !== "archive" &&
            !instance.archived)
        );
      });
    }

    return allInstances;
  };

  // Decide if pagination should be enabled
  const isPaginated = reportId
    ? workflowSettings.isReportPaginated
    : workflowSettings.isProcessTablePaginated;

  const reportIdNumber = (() => {
    if (!reportId) return null;
    const parsed = Number.parseInt(reportId, 10);
    return Number.isFinite(parsed) ? parsed : null;
  })();

  const updatingStage = (dataStages as unknown as Record<string, DataStage>)[
    "updating"
  ];

  const customColumns = useMemo(() => {
    if (reportId) {
      return reportsColumn;
    }
    return processColumns;
  }, [reportId, processColumns, reportsColumn]);

  useEffect(() => {
    if (reportId) {
      _getAllEmbeddedFields(Number.parseInt(filter?.id || "", 10));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useLayoutEffect(() => {
    if (tableRef && tableRef.current) {
      setWidth(`${tableRef.current.scrollWidth || "100%"}px`);
    }
  }, [tableRef, fields, customColumns, setWidth]);

  let filteredColumn = moveRevisionsField(
    R.filter((column: ManageViewColumn) => {
      return !R.includes(column.type, omitedFields);
    }, customColumns),
  );
  const columnVisibility = R.reduce(
    (columns: Record<string, boolean>, column: ManageViewColumn) => ({
      ...columns,
      [String(column.key || column.id)]: Boolean(column.active),
    }),
    {},
    filteredColumn,
  );

  const processColumnSize: string = filteredColumn.reduce(
    (accumulator: string, column: ManageViewColumn) => {
      const settings: Record<string, unknown> = (() => {
        try {
          return JSON.parse(column.settings || "{}") as Record<string, unknown>;
        } catch {
          return {};
        }
      })();

      if (column.type && !R.isEmpty(settings)) {
        const fields = settings["fields"];
        if (
          !R.isNil(fields) &&
          Array.isArray(fields) &&
          expandedFields[String(column.id)]
        ) {
          // Embedded fields size
          const embeddedFieldsSize = fields.reduce((accumulator: string) => {
            return `${accumulator} 230px`;
          }, getChecklistFieldSize(column.type));

          return `${accumulator} ${embeddedFieldsSize}`;
        }
      }
      return `${accumulator} ${
        column.type
          ? getChecklistFieldSize(column.type)
          : getStandardFieldSize(column.key)
      }`;
    },
    "36px ",
  );

  // Add the expanded form fields & embedded fields as columns to
  // display in the manage view
  filteredColumn = filteredColumn.reduce<Array<ManageViewColumn>>(
    (acc, column) => {
      if (column.type === "form") {
        const embeddedFormCols =
          (embeddedFormColumns as Record<string, Array<ManageViewColumn>>)[
            String(column.id)
          ] || [];
        const result = [
          ...acc,
          column,
          ...embeddedFormCols.reduce<Array<ManageViewColumn>>((acc, column) => {
            if (
              ["conversation", "childConversation", "link"].includes(
                String(column.type),
              )
            ) {
              return [
                ...acc,
                column,
                ...(((
                  embeddedConversationColumns as Record<
                    string,
                    Array<ManageViewColumn>
                  >
                )[String(column.id)] || []) as Array<ManageViewColumn>),
              ];
            }
            return [...acc, column];
          }, []),
        ];

        return result;
      } else if (
        ["conversation", "childConversation", "link"].includes(
          String(column.type),
        )
      ) {
        const embeddedColumns =
          (
            embeddedConversationColumns as Record<
              string,
              Array<ManageViewColumn>
            >
          )[String(column.id)] || [];
        const allEmbeddedColumns = embeddedColumns.reduce<
          Array<ManageViewColumn>
        >((acc, column) => {
          if (column.type === "form") {
            return [
              ...acc,
              column,
              ...(((
                embeddedFormColumns as Record<string, Array<ManageViewColumn>>
              )[String(column.id)] || []) as Array<ManageViewColumn>),
            ];
          }
          return [...acc, column];
        }, []);

        const settings = JSON.parse(
          fieldsById.toJS()[`${column.id}`]?.["settings"] || "{}",
        ) as { showMetaData?: boolean };

        const showMetaData = settings.showMetaData;

        const isVisible = customColumns.find(
          (c) => String(c.key) === String(column.id),
        )?.active;

        const isExpanded = expandedFields[`${column.id}`];

        const allColumns: Array<ManageViewColumn> = [
          ...acc,
          column,
          ...(showMetaData && isVisible && isExpanded
            ? [
                {
                  header: i18n.t(k.OWNER1),
                  label: i18n.t(k.OWNER1),
                  id: `${column.id}-owner`,
                  fieldId: `${column.id}`,
                  type: "owner",
                  key: "owner",
                  settings: i18n.t(k._4),
                  embeddedField: true,
                },
                {
                  header: i18n.t(k.STATUS1),
                  label: i18n.t(k.STATUS1),
                  id: `${column.id}-status`,
                  fieldId: `${column.id}`,
                  type: "status",
                  key: "status",
                  settings: i18n.t(k._4),
                  embeddedField: true,
                },
                {
                  header: i18n.t(k.DUEDATE),
                  label: i18n.t(k.DUEDATE),
                  id: `${column.id}-dueDate`,
                  fieldId: `${column.id}`,
                  type: "dueDate",
                  key: "dueDate",
                  settings: i18n.t(k._4),
                  embeddedField: true,
                },
              ]
            : []),
          ...(isVisible ? allEmbeddedColumns : []),
        ];

        return allColumns;
      }

      return [...acc, column];
    },
    [],
  );

  const columns = React.useMemo(
    () => [
      ...filteredColumn.map((column) => {
        const columnId = column.id ?? column.key;
        const columnIdKey = String(columnId ?? "");
        const columnKey =
          typeof column.type === "string"
            ? column.type
            : typeof column.key === "string"
              ? column.key
              : undefined;
        const defaultColumnWidth = getManageViewFieldSize(columnKey);
        const columnWidth = reportId
          ? (fieldSizeMap[columnIdKey] ?? defaultColumnWidth)
          : defaultColumnWidth;

        const parsedWidth = Number.parseInt(
          String(columnWidth ?? "0px").split("px")[0] || "0",
          10,
        );

        return {
          header:
            typeof column.key === "string"
              ? convertToTitleCase(column.key)
              : column.label,

          ...(column.accessorFn
            ? { accessorFn: column.accessorFn, accessorKey: `${column.id}` }
            : { accessorKey: column.id ? `${column.id}` : column.key }),
          type: column.type,
          settings: column.settings,
          fieldId: column.fieldId || column.id,
          formIndex: column.formIndex,
          formId: column.formId,
          formField: column.formField,
          showTitle: column.showTitle,
          embeddedField: column.embeddedField,
          minSize: 50,
          size: Number.isFinite(parsedWidth) ? parsedWidth : 50,
          ...getHeaderColumnSettings(
            column.type ?? null,
            (column.key ?? column.id ?? "") as string | number,
            processColumns,
            currentUser?.uid ?? null,
          ),
        } as ManageViewColumnDef;
      }),
    ],

    // Keep dependencies aligned with existing behavior
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredColumn, filter],
  ) as Array<ManageViewColumnDef>;

  const [allInstances, setAllInstances] = useState<Array<TableRow>>([]);
  const sortColumns = filter.sort || [];
  const filterParams = R.omit(
    ["id", "title", "page", "reportId", "chartId", "sort"],
    filter || {},
  ) as Record<string, unknown> & { status?: Array<number | string> };

  const setFilterParams = (filter: Record<string, unknown>) => {
    setManageViewFilter({ ...filter, sort: sortColumns });
  };
  const setSortColumns = (sort: Array<string>) => {
    setManageViewFilter({ ...filterParams, sort });
  };

  useEffect(() => {
    if (!reportId || !areInstancesFormatted) return;

    const appliedFilters = R.omit(
      ["deepFilter"],
      filter as unknown as Record<string, unknown>,
    ) as Record<string, unknown>;

    const updatedInstances: Record<string, WorkflowInstances> = {};

    const workerData = {
      fieldsById: fieldsById.toJS(),
      instancesById,
      filters: appliedFilters,
      chatroomAttributes: ["owner", "status", "dueDate"],
      updatedInstances,
      allRecords,
      payload: appliedFilters,
    };

    filterWorker?.postMessage(workerData, (result) => {
      _filterEmbeddedFields({
        instances: result as Record<string, WorkflowInstances>,
      });
      setFilterParams(appliedFilters);
    });
  }, [reportId, areInstancesFormatted]);

  useEffect(() => {
    setAllInstances(
      getInstances(instances, filterParams, userNames, templateStatus),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances, filter]);

  const data = React.useMemo(() => allInstances, [allInstances]);
  const tableInstance = useReactTable<TableRow>({
    columns,
    data,
    columnResizeMode,
    defaultColumn: {
      size: 50,
    },
    state: {
      columnVisibility,
      ...(isPaginated ? { pagination } : {}),
    },
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getDefaultFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(isPaginated ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    onPaginationChange: setPagination,
  });
  const {
    getHeaderGroups,
    getRowModel,
    setSorting,
    getPrePaginationRowModel,
    getFilteredRowModel,
    setColumnFilters,
  } = tableInstance;
  const rows = getRowModel().rows;
  const headerGroups = getHeaderGroups();

  // Update the redux store with the filtered rows
  // Only for making sure that users can apply filters by clicking on
  // charts in report
  useEffect(() => {
    const filtered = getFilteredRowModel().rows.map((row) => row.original);
    if (
      reportId &&
      Number.parseInt(reportId, 10) &&
      getFilteredRowModel().rows &&
      allInstances.length > 0 &&
      allInstances.length > filtered.length
    ) {
      const filteredById = filtered.reduce<Record<string, WorkflowInstances>>(
        (acc, instance) => {
          acc[String(instance.chatroomId)] =
            instance as unknown as WorkflowInstances;
          return acc;
        },
        {},
      );
      dispatch(
        filterReportInstances(Number.parseInt(reportId, 10), filteredById),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instances.length]);

  useEffect(() => {
    if (areInstancesFormatted) {
      const filters = R.omit(
        ["id", "page", "reportId", "chartId", "sort"],
        filter || {},
      ) as Record<string, unknown>;
      const filterArr: ColumnFiltersState = [];
      const columnsWithAccessor = columns.filter(
        (column) => typeof column.accessorKey === "string",
      );

      for (const filterItem in filters) {
        // Only add filters for visible columns
        if (
          columnsWithAccessor.some(
            (column) => column.accessorKey === filterItem,
          )
        ) {
          filterArr.push({
            id: filterItem,
            value: (filters as Record<string, unknown>)[filterItem],
          });
        }
      }

      setColumnFilters(filterArr);
      setSorting(
        (filter.sort || []).map((col: string) => ({
          id: col.split(":")[0] || "",
          desc: col.split(":")[1] === "desc",
        })) as SortingState,
      );
    }
  }, [filter, areInstancesFormatted]);

  const renderRow = (index: number) => {
    const row = rows[index];

    if (!row) return null;

    const process = row.original;

    if (
      process &&
      bulkUpdateStatus !== updatingStage &&
      (row.original.currentVersion ||
        showVersions.get(`${row.original.seqNo}`) ||
        allRevisionsShown)
    ) {
      return (
        <Row
          row={row}
          process={process}
          width={width}
          columnSize={processColumnSize}
          roomId={process.chatroomId}
          index={process.index}
          numberOfColumns={filteredColumn.length + 1}
          processColumns={filteredColumn}
          allRevisionsShown={allRevisionsShown}
          disableSelector={!manageViewAllowed} // Disable bulk update if accessing without manage permission
        />
      );
    }

    return null;
  };

  const [debouncedsetFilter] = useDebouncedCallback(
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setFilterParams({
        ...filterParams,
        title: e.target.value,
      }),
    400,
  );

  const setSort = (
    order: boolean,
    column: string,
    toggleSortBy: (desc?: boolean, isMulti?: boolean) => void,
  ) => {
    const sortColumn = `${column}:${order ? "desc" : "asc"}`;

    const columnIndex = R.indexOf(
      column,
      sortColumns.map((col: string) => col.split(":")[0]),
    );
    if (columnIndex !== -1) {
      setSortColumns(R.update(columnIndex, sortColumn, sortColumns));
    } else {
      setSortColumns([...sortColumns, sortColumn]);
    }
    toggleSortBy(order, true);
  };

  // Handle filtering on a column
  const handleFilter = (column: string, value: unknown) => {
    setFilterParams({ ...filterParams, [column]: value });
  };

  // Remove applied filter
  const removeFilter = (column: string, value: unknown) => {
    const currentValue = (filterParams as Record<string, unknown>)[column];
    if (Array.isArray(currentValue) && currentValue.length) {
      const newValue = currentValue.filter((val) => val !== value);
      setFilterParams({ ...filterParams, [column]: newValue });
    } else {
      setFilterParams(
        R.omit([column], filterParams) as Record<string, unknown>,
      );
    }
  };

  // Remove applied sort
  const removeSort = (column: string) => {
    const columnIndex = R.indexOf(
      column.split(":")[0],
      sortColumns.map((col: string) => col.split(":")[0]),
    );
    if (columnIndex !== -1) {
      const newSortColummns = R.remove(columnIndex, 1, sortColumns);
      setSortColumns(newSortColummns);
      setSorting(
        newSortColummns.map((col: string) => ({
          id: col.split(":")[0] || "",
          desc: col.split(":")[1] === "desc",
        })),
      );
    }
  };

  const clearSort = () => {
    const updatedInstances = { ...instancesById };

    const workerData = {
      fieldsById: fieldsById.toJS(),
      instancesById,
      filters: filter,
      chatroomAttributes: ["owner", "dueDate", "status"],
      updatedInstances,
      payload: {},
    };

    filterWorker?.postMessage(workerData, (result) => {
      _filterEmbeddedFields({
        instances: result as Record<string, WorkflowInstances>,
      });
    });

    setSorting([]);
    setManageViewFilter({});
    clearAllRecordsFilter();
  };

  const handleCreateNewChart = () => {
    reportId && dispatch(setReports(reportId));

    dispatch(closeNewChartDialog());

    dispatch(
      setNewChartDialogAttributes({
        show: true,
      }),
    );
  };

  // Calculate the total number of rows to be displayed for that process template
  const totalRowCount = isPaginated
    ? Object.values(tableInstance.getPaginationRowModel()?.rowsById || {})
        .length
    : rows.length;

  return (
    <>
      <SearchComponent
        onChange={debouncedsetFilter}
        reportId={reportIdNumber}
        disableActions={!manageViewAllowed}
      />
      <MenuBarComponent
        rowCount={totalRowCount}
        reportId={reportIdNumber}
        getHeaderGroups={getHeaderGroups}
        getRowModel={getPrePaginationRowModel}
        setRevisionsShown={setRevisionsShown}
        allRevisionsShown={allRevisionsShown}
      />

      <Filters
        sortColumns={sortColumns}
        clearSort={clearSort}
        removeSort={removeSort}
        filterParams={filterParams}
        removeFilter={removeFilter}
      />

      <TableWrapper>
        <StyledTableComponent
          style={{
            width: tableInstance.getCenterTotalSize(),
          }}
          columnSize={processColumnSize}
          ref={tableRef}
        >
          <StyledHeader>
            {isPaginated ? (
              <SelectAll
                getRowModel={tableInstance.getPaginationRowModel}
                disabled={!manageViewAllowed}
              />
            ) : (
              <Selector />
            )}
            {headerGroups.map((headerGroup, index) => (
              <React.Fragment key={index}>
                {headerGroup.headers.map((header) => {
                  const headerTyped = header;
                  const column = headerTyped.column
                    .columnDef as ManageViewColumnDef;

                  const fieldIdNum = Number.parseInt(
                    String(column.fieldId ?? ""),
                    10,
                  );
                  const hasFieldId = Number.isFinite(fieldIdNum);

                  return hasFieldId ? (
                    <NestedTable key={headerTyped.id}>
                      <thead>
                        <tr>
                          {column.embeddedField ? (
                            <EmbeddedFieldsHeader>
                              {i18n.t(k._24)}
                            </EmbeddedFieldsHeader>
                          ) : null}
                          {column.formField ? (
                            <FormFieldsHeaderComponent
                              width={
                                headerTyped.getSize() ??
                                getChecklistFieldSize(String(column.type))
                              }
                              backgroundColor={
                                (column.formIndex || 0) % 2 === 0
                                  ? colors.purpleLight
                                  : colors.greenLight
                              }
                            >
                              {column.showTitle ? (
                                <ChakraText
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  margin={0}
                                  maxWidth={headerTyped.getSize()}
                                >
                                  <FormName id={column.formId} />
                                  form
                                </ChakraText>
                              ) : null}
                            </FormFieldsHeaderComponent>
                          ) : null}
                        </tr>
                        <tr>
                          <ChecklistHeaderItemComponent
                            key={headerTyped.id}
                            column={headerTyped.id}
                            fieldId={column.fieldId}
                            label={
                              typeof column.header === "string"
                                ? column.header
                                : undefined
                            }
                            type={column.type}
                            settings={column.settings}
                            setFilter={handleFilter}
                            filter={
                              (filterParams as Record<string, unknown>)[
                                headerTyped.id
                              ]
                            }
                            toggleSortBy={(order?: boolean) =>
                              setSort(
                                Boolean(order),
                                headerTyped.id,
                                headerTyped.column.toggleSorting,
                              )
                            }
                            childColumn={Boolean(
                              column.embeddedField || column.formField,
                            )}
                            formIndex={column.formIndex}
                            isSortedDesc={Boolean(column.isSortedDesc)}
                            header={headerTyped}
                          />
                        </tr>
                      </thead>
                    </NestedTable>
                  ) : (
                    <HeaderItemComponent
                      key={headerTyped.id}
                      column={headerTyped.id}
                      setFilter={handleFilter}
                      filter={
                        (filterParams as Record<string, unknown>)[
                          headerTyped.id
                        ]
                      }
                      toggleSortBy={(order?: boolean) =>
                        setSort(
                          Boolean(order),
                          headerTyped.id,
                          headerTyped.column.toggleSorting,
                        )
                      }
                      header={headerTyped}
                    />
                  );
                })}
              </React.Fragment>
            ))}
          </StyledHeader>
          <VirtualListComponent
            initialAmount={50}
            progressiveAmount={50}
            role="list"
            renderItem={renderRow}
            renderLoader={() => (
              <RenderingLoader
                columnSize={processColumnSize}
                numberOfColumns={filteredColumn.length + 1}
                loading
              />
            )}
            rowCount={instances.length}
          />

          <Loader
            columnSize={processColumnSize}
            numberOfColumns={filteredColumn.length + 1}
          />

          {(rows.length === 0 || instances.length === 0) && <NoValue />}
        </StyledTableComponent>
      </TableWrapper>
      {isPaginated && (
        <Footer>
          <PageButton
            className="border rounded p-1"
            onClick={() => tableInstance.previousPage()}
            disabled={!tableInstance.getCanPreviousPage()}
          >
            {"<"}
          </PageButton>
          <PageButton
            className="border rounded p-1"
            onClick={() => tableInstance.nextPage()}
            disabled={!tableInstance.getCanNextPage()}
          >
            {">"}
          </PageButton>
          <PageIndicatorContainer>
            <div>{i18n.t(k.PAGE)}</div>
            <strong>
              {tableInstance.getState().pagination.pageIndex + 1} {i18n.t(k.OF)}{" "}
              {tableInstance.getPageCount().toLocaleString()}
            </strong>
          </PageIndicatorContainer>
          <PageIndicatorContainer>
            {i18n.t(k.GO_TO_PAGE)}
            <PageInput
              type="number"
              defaultValue={tableInstance.getState().pagination.pageIndex + 1}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                tableInstance.setPageIndex(page);
              }}
              className="border p-1 rounded w-16"
            />
          </PageIndicatorContainer>
          <select
            value={tableInstance.getState().pagination.pageSize}
            onChange={(e) => {
              tableInstance.setPageSize(Number(e.target.value));
            }}
          >
            {[16, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {i18n.t(k.SHOW)} {pageSize}
              </option>
            ))}
          </select>
        </Footer>
      )}

      {chartId && reportId && (
        <FlexComponent sx={styles.FooterButtonsWrapper}>
          <LinkComponent to={setReports(reportId)} style={styles.CancelButton}>
            {i18n.t(k.BACK_TO_REPORT)}
          </LinkComponent>
          <ChakraButton
            onClick={handleCreateNewChart}
            sx={{ borderRadius: "4px" }}
            variant="uPrimary"
          >
            {i18n.t(k.CREATE_ANOTHER_CHART)}
          </ChakraButton>
        </FlexComponent>
      )}
    </>
  );
};

const mapStateToProps = ({ app }: { app: AppState }) => {
  const filter = getWorkflowInstanceFilter(app);
  const instances = getWorkflowInstances(app);
  return {
    fields: getPrincipalChecklistFields(app),
    reportId: filter.reportId,
    instancesById: getWorkflowInstancesById(app),
    processColumns: getProcessTableColumns(app, filter.id || ""),
    reportsColumn: getReportsTableColumns(app, filter.reportId || ""),
    expandedFields: getExpandedFields(app),
    instances,
    userNames: getUserNames(app),
    filter,
    templateStatus: getTemplateStatus(app, filter.id || ""),
    showVersions: getShowVersionInfo(app),
    embeddedFormColumns: getEmbeddedFormColumns(app),
    embeddedConversationColumns: getEmbeddedConversationColumns(app),
    fieldsById: getChecklistFieldsById(app),
    fieldSizeMap: getFieldSizeMap(app),
    allRecords: getAllRecords(app),
    areInstancesFormatted: getAreInstancesFormatted(app),
    workflowSettings: getWorkflowSettings(app, filter?.id || ""),
    bulkUpdateStatus: getBulkUpdateStatus(app),
    currentUser: getCurrentUser(app),
  };
};

export default connect(mapStateToProps, {
  setManageViewFilter,
  _getAllEmbeddedFields: getAllEmbeddedFields,
  clearAllRecordsFilter,
  _filterEmbeddedFields: filterEmbeddedFields,
})(Table);
